import { z } from 'zod';

export const callDirectionSchema = z.enum(['OUTBOUND', 'INBOUND']);

export type CallDirection = z.infer<typeof callDirectionSchema>;

export const callTransportSchema = z.enum(['PSTN', 'WEBRTC', 'WHATSAPP', 'WEB_WIDGET', 'EMAIL']);

export type CallTransport = z.infer<typeof callTransportSchema>;

/**
 * Call status enum matching Twilio call statuses
 * @see https://www.twilio.com/docs/voice/api/call-resource#call-status-values
 * @see https://help.twilio.com/articles/223132547-What-are-the-Possible-Call-Statuses-and-What-do-They-Mean
 */

export const callStatusSchema = z.enum([
  'QUEUED',
  'INITIATED',
  'RINGING',
  'IN_PROGRESS',
  'CANCELED',
  'COMPLETED',
  'BUSY',
  'NO_ANSWER',
  'FAILED',
]);

export type CallStatus = z.infer<typeof callStatusSchema>;

/**
 * Outcomes are dynamic: the vocabulary lives in the outcome_definitions table
 * (platform-wide) and calls store the key as
 * plain text — so the schema accepts any key string.
 *
 * KNOWN_CALL_OUTCOMES lists the seeded legacy keys, kept only as a fallback
 * for UIs that render before the dynamic list has loaded.
 */
export const KNOWN_CALL_OUTCOMES = [
  'PICKED_UP',
  'NO_ANSWER',
  'BUSY',
  'VOICEMAIL',
  'AVAILABILITY_CAPTURED',
  'FUTURE_AVAILABILITY',
  'LEAD_CAPTURED',
  'ALREADY_PURCHASED',
  'CALLBACK_REQUESTED',
  'SHIFT_CONFIRMED',
  'SHIFT_DECLINED',
  'TRANSFERRED_TO_HUMAN',
  'CANCELED',
  'FAILED',
  'FINANCE_INQUIRY',
  'REFUND_REQUEST',
  'ADMINISTRATIVE',
  'ATTENDING_USD_EVENT',
  'NOT_ATTENDING_USD_EVENT',
  'AVAILABLE_COOK',
  'NOT_AVAILABLE_COOK',
] as const;

export const callOutcomeSchema = z.string().min(1).max(100);

export type CallOutcome = z.infer<typeof callOutcomeSchema>;

/** A row of the outcome_definitions table (dynamic outcome vocabulary). */
export const outcomeDefinitionSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(100),
  label: z.string().min(1).max(255),
  color: z.string(),
  sort_order: z.number().int(),
  active: z.boolean(),
  is_system: z.boolean(),
  counts_as_answered: z.boolean(),
  expects_data_capture: z.boolean(),
  is_success: z.boolean(),
  is_retryable: z.boolean(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

export type OutcomeDefinition = z.infer<typeof outcomeDefinitionSchema>;

/** Payload for creating/updating an outcome definition. */
export const outcomeDefinitionInputSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Key must be UPPER_SNAKE_CASE'),
  label: z.string().min(1).max(255),
  color: z.string().optional(),
  sort_order: z.number().int().optional(),
  active: z.boolean().optional(),
  counts_as_answered: z.boolean().optional(),
  expects_data_capture: z.boolean().optional(),
  is_success: z.boolean().optional(),
  is_retryable: z.boolean().optional(),
});

export type OutcomeDefinitionInput = z.infer<typeof outcomeDefinitionInputSchema>;

export const callConsentFilterSchema = z.enum(['consented', 'declined']);

export type CallConsentFilter = z.infer<typeof callConsentFilterSchema>;

export const autoCorrectionDataSchema = z.object({
  corrected_at: z.string().datetime(),
  corrected_fields: z.array(z.string()),
  original_values: z.record(z.string(), z.any()),
});

export type AutoCorrectionData = z.infer<typeof autoCorrectionDataSchema>;

export const callRecordSchema = z.object({
  id: z.string().uuid(),
  contact_id: z.string().uuid().nullable().optional(),
  configuration_id: z.string().uuid().nullable().optional(),
  configuration_version_id: z.string().uuid().nullable().optional(),
  direction: callDirectionSchema,
  call_transport: callTransportSchema.optional(),
  // Provider call SID; text sessions are prefixed "text-" (matches the
  // calls.modality backfill discriminator).
  call_sid: z.string().nullable().optional(),
  // VOICE call vs TEXT chat session (transport alone can't tell).
  modality: z.enum(['VOICE', 'TEXT']).optional(),
  status: callStatusSchema,
  outcome: callOutcomeSchema,
  availability_data: z.record(z.string(), z.any()).nullable().optional(),
  lead_data: z.record(z.string(), z.any()).nullable().optional(),
  callback_data: z.record(z.string(), z.any()).nullable().optional(),
  attempt_number: z.number().int().min(1).optional(),
  recording_path: z.string().optional(), // GCS file path (provider recording), not URL
  internal_recording_path: z.string().optional(), // GCS file path (server-side mixed recording)
  transcript: z.any().optional(), // Full call transcript JSON
  duration: z.number().int().nonnegative(),
  executed_at: z.string().datetime(),
  phone_number: z.string().nullable().optional(), // Phone number from calls table (for inbound calls)
  auto_corrected: z.boolean().optional(),
  auto_correction_data: autoCorrectionDataSchema.nullable().optional(),
  human_reviewed: z.boolean().optional(),
  is_reviewed: z.boolean().optional(),
});

export type CallRecord = z.infer<typeof callRecordSchema>;

// Call query schema for pagination and filtering
export const callQuerySchema = z.object({
  // Pagination
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).optional(),

  // Enum filters
  status: callStatusSchema.optional(),
  outcome: callOutcomeSchema.optional(),
  direction: callDirectionSchema.optional(),
  call_transport: callTransportSchema.optional(),
  consent: callConsentFilterSchema.optional(),

  // Relationship filters
  campaign_id: z.string().uuid().optional(),
  contact_id: z.string().uuid().optional(),
  configuration_id: z.string().uuid().optional(),

  // Date range filter for executed_at
  executedAtFrom: z.coerce.date().optional(),
  executedAtTo: z.coerce.date().optional(),

  // Text search on phone_number
  search: z.string().optional(),

  // Duration range filter (in seconds)
  durationMin: z.coerce.number().int().min(0).optional(),
  durationMax: z.coerce.number().int().min(0).optional(),

  // Attempt number filter
  attemptNumber: z.coerce.number().int().min(1).optional(),

  // Review status filter
  is_reviewed: z.coerce.boolean().optional(),

  // Sorting
  sortBy: z
    .enum(['executed_at', 'created_at', 'duration', 'status', 'outcome'])
    .default('executed_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type CallQuery = z.infer<typeof callQuerySchema>;
